import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { toast } from 'sonner';
import { cyclesAPI, restaurantsAPI, contractsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Layers, GraduationCap, Building2,
  Copy, Trash2, XCircle, Plus, CheckCircle2, AlertTriangle, Save, LayoutGrid, List as ListIcon, FileSignature,
} from 'lucide-react';
import CycleBoard from '../components/CycleBoard';
import CyclePlacementsList from '../components/CyclePlacementsList';
import CycleParticipants from '../components/CycleParticipants';
import RestaurantDrawer from '../components/RestaurantDrawer';
import CohortFields from '../components/CohortFields';
import PlacementDocuments from './PlacementDocuments';
import AllFeedback from './AllFeedback';
import BulkLettersDialog from '../components/BulkLettersDialog';
import PhasesEditor from '../components/PhasesEditor';
import PhaseTimeline from '../components/PhaseTimeline';
import { Sparkles, Megaphone, CalendarRange, RefreshCw, Loader2, Wrench } from 'lucide-react';

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed: 'bg-blue-100 text-blue-700 border-blue-200',
  archived: 'bg-amber-50 text-amber-800 border-amber-200',
};

const CycleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isReadOnly } = useAuth();
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [addRestOpen, setAddRestOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [refreshingLetters, setRefreshingLetters] = useState(false);
  const [docsKey, setDocsKey] = useState(0);
  const refreshLetters = async () => {
    setRefreshingLetters(true);
    try {
      const r = await contractsAPI.bulkRefresh(id);
      toast.success(`Refreshed ${r.data.refreshed} unsigned letter${r.data.refreshed === 1 ? '' : 's'} with the current phases`);
      setDocsKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingLetters(false);
    }
  };
  const [repairing, setRepairing] = useState(false);
  const repairMoved = async () => {
    setRepairing(true);
    try {
      const r = await contractsAPI.repairMovedPlacements(id);
      const s = r.data;
      if (!s.placements_fixed) {
        toast.success(`Checked ${s.placements_checked} placements — every letter and assessment form already matches its restaurant`);
      } else {
        toast.success(`Repaired ${s.placements_fixed} placement${s.placements_fixed === 1 ? '' : 's'} (${s.fixed.map((f) => f.student_name).join(', ')}) — ${s.letters_refreshed} letters re-pointed, ${s.letters_reissued} re-issued, ${s.forms} assessment forms updated`);
        setDocsKey((k) => k + 1);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };
  const [bulkMode, setBulkMode] = useState('placement');
  const [orphanCount, setOrphanCount] = useState(0);
  const [orphansOpen, setOrphansOpen] = useState(false);
  const [boardView, setBoardView] = useState('board');
  const [drawerRestaurant, setDrawerRestaurant] = useState(null);
  const [drawerTab, setDrawerTab] = useState('placements');

  const load = useCallback(async ({ silent = false } = {}) => {
    // Only flash the "Loading cycle…" placeholder on the first fetch. On
    // subsequent refreshes (after a drop on the board, roster edit, etc.)
    // we swap in the new data silently so the Tabs component isn't
    // unmounted — otherwise the user gets kicked back to the default tab.
    if (!silent) setLoading(true);
    try {
      const [c, r, o] = await Promise.all([cyclesAPI.get(id), restaurantsAPI.getAll(), cyclesAPI.listOrphans(id).catch(() => ({ data: { orphans: [] } }))]);
      setCycle(c.data);
      setAllRestaurants(r.data || []);
      setOrphanCount((o.data?.orphans || []).length);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cycle');
      navigate('/cycles');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates, successMsg) => {
    try {
      const r = await cyclesAPI.update(id, updates);
      setCycle(r.data);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleAddRestaurant = async (rid) => {
    try {
      const r = await cyclesAPI.addRestaurant(id, rid);
      setCycle(r.data);
      toast.success('Restaurant added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDuplicate = async () => {
    try {
      const r = await cyclesAPI.duplicate(id);
      toast.success('Cycle duplicated as draft');
      navigate(`/cycles/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this cycle? Only cycles with zero placements can be deleted.')) return;
    try {
      await cyclesAPI.remove(id);
      toast.success('Cycle deleted');
      navigate('/cycles');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading || !cycle) return <Layout><div className="p-6 text-gray-500">Loading cycle…</div></Layout>;

  const availableRestaurants = allRestaurants.filter((r) => !(cycle.restaurant_ids || []).includes(r.id));
  const canWrite = isAdmin && !isReadOnly && cycle.status !== 'archived';
  const livePlacements = (cycle.placements || []).filter((p) => p.status === 'active' || p.status === 'pending');
  const placedStudents = new Set(livePlacements.map((p) => p.student_id)).size;
  const totalSeats = (cycle.restaurants || []).reduce((n, r) => n + (r.available_positions || 0), 0);
  const openRestaurantDrawer = (r, tab = 'placements') => {
    setDrawerTab(tab);
    setDrawerRestaurant(allRestaurants.find((x) => x.id === r.id) || r);
  };

  return (
    <Layout>
      <div className="space-y-4 animate-fade-in" data-testid={`cycle-detail-${id}`}>
        <button onClick={() => navigate('/cycles')} className="text-xs text-gray-500 hover:text-ica-navy inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to cycles
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Layers className="w-6 h-6 text-ica-navy" />
              <h1 className="font-heading text-2xl font-bold text-ica-navy truncate">{cycle.name}</h1>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${STATUS_STYLE[cycle.status] || STATUS_STYLE.draft}`}>
                {cycle.status}
              </span>
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Class of {cycle.year_of_enrollment} · {cycle.year_of_study} · {cycle.course_length} · {cycle.course_type} ·{' '}
              <span className="text-gray-800 font-medium">{cycle.start_date}</span> → <span className="text-gray-800 font-medium">{cycle.end_date}</span>
              {(cycle.phases || []).length > 0 && (
                <span data-testid="cycle-phase-chip" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-ica-navy/10 text-ica-navy align-middle">
                  <CalendarRange className="w-3 h-3" /> {cycle.phases.length} phase{cycle.phases.length === 1 ? '' : 's'}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {orphanCount > 0 && (
              <button
                type="button"
                data-testid="orphans-badge"
                onClick={() => setOrphansOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> {orphanCount} orphan placement{orphanCount === 1 ? '' : 's'}
              </button>
            )}
          {canWrite && (
            <>
              {cycle.status === 'draft' && (
                <Button data-testid="activate-cycle" size="sm" onClick={() => patch({ status: 'active' }, 'Cycle activated')} className="bg-emerald-600 hover:bg-emerald-700 text-white">Activate</Button>
              )}
              {cycle.status === 'active' && (
                <Button data-testid="close-cycle" size="sm" onClick={() => patch({ status: 'closed' }, 'Cycle closed')} variant="outline">Close cycle</Button>
              )}
              {cycle.status === 'closed' && (
                <Button data-testid="archive-cycle" size="sm" onClick={() => patch({ status: 'archived' }, 'Cycle archived')} variant="outline">Archive</Button>
              )}
              <Button data-testid="duplicate-cycle" size="sm" variant="outline" onClick={handleDuplicate}>
                <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
              </Button>
              {cycle.placement_count === 0 && (
                <Button data-testid="delete-cycle" size="sm" variant="ghost" onClick={handleDelete} className="text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </>
          )}
          </div>
        </div>

        {/* Rollup */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cycle-rollup">
          {[
            { key: 'placed', label: 'Students placed', value: placedStudents, total: cycle.students.length, Icon: GraduationCap, bar: 'bg-ica-gold' },
            { key: 'seats', label: 'Seats filled', value: livePlacements.length, total: totalSeats, Icon: Building2, bar: 'bg-ica-navy' },
            { key: 'letters', label: 'Student letters signed', value: cycle.letters_signed || 0, total: livePlacements.length, Icon: FileSignature, bar: 'bg-emerald-500', tone: 'text-emerald-700' },
            { key: 'industry', label: 'Industry letters signed', value: cycle.industry_letters_signed || 0, total: cycle.industry_letters_total || 0, Icon: Megaphone, bar: 'bg-sky-500', tone: 'text-sky-700', hint: 'restaurants' },
            { key: 'overdue', label: 'Assessments overdue', value: cycle.assessments_overdue, Icon: AlertTriangle, tone: cycle.assessments_overdue > 0 ? 'text-red-600' : 'text-gray-500', danger: cycle.assessments_overdue > 0 },
          ].map(({ key, label, value, total, Icon, tone, bar, danger, hint }) => {
            const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
            return (
              <Card key={key} className={danger ? 'border-red-200 bg-red-50/60' : ''} data-testid={`rollup-${key}`}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-[11px] uppercase tracking-wide ${danger ? 'text-red-700' : 'text-gray-500'}`}>{label}</p>
                      <p className={`font-heading text-2xl font-bold ${tone || 'text-ica-navy'}`}>
                        {value}{total !== undefined && <span className="text-sm text-gray-400 font-medium"> / {total}{hint ? ` ${hint}` : ''}</span>}
                      </p>
                    </div>
                    <Icon className={`w-6 h-6 ${tone || 'text-ica-navy'} opacity-60`} />
                  </div>
                  {total !== undefined && (
                    <div className="h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                      <div className={`h-full ${bar} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="overview">
          <div className="tabs-scroll -mx-1 px-1 pb-1">
          <TabsList className="inline-flex w-max">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="participants" data-testid="tab-participants">Participants ({cycle.students.length} · {cycle.restaurants.length})</TabsTrigger>
            <TabsTrigger value="board" data-testid="tab-board" className="inline-flex items-center gap-1">
              <LayoutGrid className="w-3.5 h-3.5" /> Board ({livePlacements.length})
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="overview">
            <div className="space-y-4">
            {(cycle.phases || []).length > 0 && (
              <Card><CardContent className="pt-5 pb-4"><PhaseTimeline phases={cycle.phases} cycleId={id} /></CardContent></Card>
            )}
            <Card><CardContent className="pt-6 space-y-3 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded bg-emerald-50 border border-emerald-100">
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Contracts signed</div>
                  <div className="text-2xl font-bold text-emerald-800">{cycle.contracts_signed}</div>
                  <div className="text-[11px] text-emerald-700 mt-0.5">of {cycle.contracts_signed + cycle.contracts_pending}</div>
                </div>
                <div className="p-3 rounded bg-amber-50 border border-amber-100">
                  <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Contracts pending</div>
                  <div className="text-2xl font-bold text-amber-800">{cycle.contracts_pending}</div>
                </div>
                <div className="p-3 rounded bg-sky-50 border border-sky-100">
                  <div className="text-[11px] uppercase tracking-wide text-sky-700 font-semibold">Assessments submitted</div>
                  <div className="text-2xl font-bold text-sky-800">{cycle.assessments_submitted}</div>
                  <div className="text-[11px] text-sky-700 mt-0.5">of {cycle.assessments_submitted + cycle.assessments_pending}</div>
                </div>
                <div className={`p-3 rounded border ${cycle.assessments_overdue > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                  <div className={`text-[11px] uppercase tracking-wide font-semibold ${cycle.assessments_overdue > 0 ? 'text-red-700' : 'text-gray-600'}`}>Assessments overdue</div>
                  <div className={`text-2xl font-bold ${cycle.assessments_overdue > 0 ? 'text-red-800' : 'text-gray-800'}`}>{cycle.assessments_overdue}</div>
                </div>
              </div>
              {cycle.notes && (
                <div className="pt-2 border-t"><p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Notes</p><p className="text-gray-800 text-sm">{cycle.notes}</p></div>
              )}
            </CardContent></Card>
            </div>
          </TabsContent>

          <TabsContent value="participants">
            <CycleParticipants
              cycle={cycle}
              canWrite={canWrite}
              onCycle={setCycle}
              onAddRestaurant={() => setAddRestOpen(true)}
              onRestaurantClick={openRestaurantDrawer}
              onIndustryLetterClick={(r) => openRestaurantDrawer(r, 'industry')}
            />
          </TabsContent>

          <TabsContent value="board">
            <div className="space-y-3">
              <div className="flex justify-end">
                <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" data-testid="board-view-toggle">
                  {[['board', LayoutGrid, 'Board'], ['list', ListIcon, 'List']].map(([v, Icon, label]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`board-view-${v}`}
                      onClick={() => setBoardView(v)}
                      className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${boardView === v ? 'bg-ica-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>
              </div>
              {boardView === 'board' ? (
                <CycleBoard cycle={cycle} canWrite={canWrite} onChange={() => load({ silent: true })} onRestaurantClick={(r) => openRestaurantDrawer(r, 'details')} />
              ) : (
                <CyclePlacementsList cycle={cycle} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="documents">
            <div className="space-y-4">
              {canWrite && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3">
                  <div>
                    <div className="font-semibold text-sm text-ica-navy">Bulk letters for this cycle</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Generate draft letters and email them out — scoped to this cycle only. Existing non-voided letters are skipped automatically.
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="cycle-refresh-letters"
                      variant="ghost"
                      className="text-gray-600"
                      disabled={refreshingLetters}
                      title="Re-sync every draft / sent letter in this cycle with the current phases and template"
                      onClick={refreshLetters}
                    >
                      {refreshingLetters ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Refresh unsigned letters
                    </Button>
                    <Button
                      data-testid="cycle-repair-moved-placements"
                      variant="ghost"
                      className="text-gray-600"
                      disabled={repairing}
                      title="Find students whose letter or assessment forms still point at a previous restaurant and bring them in line"
                      onClick={repairMoved}
                    >
                      {repairing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />} Repair moved placements
                    </Button>
                    <Button
                      data-testid="cycle-bulk-placement-letters"
                      variant="outline"
                      onClick={() => { setBulkMode('placement'); setBulkOpen(true); }}
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Bulk student placement letters
                    </Button>
                    <Button
                      data-testid="cycle-bulk-industry-letters"
                      variant="outline"
                      onClick={() => { setBulkMode('industry'); setBulkOpen(true); }}
                    >
                      <Megaphone className="w-4 h-4 mr-2" /> Bulk industry letters
                    </Button>
                  </div>
                </div>
              )}
              <PlacementDocuments key={docsKey} scopedCycleId={id} hideLayout />
            </div>
          </TabsContent>

          <TabsContent value="feedback">
            <AllFeedback scopedCycleId={id} hideLayout />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab cycle={cycle} onSaved={() => load({ silent: true })} canWrite={canWrite} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk letters wizard, pre-scoped to this cycle */}
      <BulkLettersDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        presetCycle={cycle}
        presetMode={bulkMode}
        onCompleted={() => load({ silent: true })}
      />

      {/* Add restaurant dialog */}
      <Dialog open={addRestOpen} onOpenChange={setAddRestOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add restaurant to cycle</DialogTitle>
            <DialogDescription>Only restaurants not already in the cycle are shown.</DialogDescription>
          </DialogHeader>
          {availableRestaurants.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-4">All restaurants are already in this cycle.</p>
          ) : (
            <ul className="divide-y max-h-96 overflow-y-auto">
              {availableRestaurants.map((r) => (
                <li key={r.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{r.name}</div>
                    <div className="text-[11px] text-gray-500">{r.contact_person} · {r.available_positions} positions</div>
                  </div>
                  <Button data-testid={`pick-restaurant-${r.id}`} size="sm" onClick={() => handleAddRestaurant(r.id)}>Add</Button>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAddRestOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orphans — only reachable via the header badge when count > 0 */}
      <Dialog open={orphansOpen} onOpenChange={setOrphansOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="orphans-dialog">
          <DialogHeader>
            <DialogTitle className="text-ica-navy flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Orphan placements</DialogTitle>
            <DialogDescription>Placements still linked to this cycle whose student no longer matches the cohort.</DialogDescription>
          </DialogHeader>
          <OrphansTab cycleId={id} canWrite={canWrite} onChange={() => load({ silent: true })} />
        </DialogContent>
      </Dialog>

      <RestaurantDrawer
        restaurant={drawerRestaurant}
        open={!!drawerRestaurant}
        onOpenChange={(o) => { if (!o) setDrawerRestaurant(null); }}
        initialTab={drawerTab}
        focusCycleId={id}
        canWrite={isAdmin && !isReadOnly}
        onUpdated={() => load({ silent: true })}
      />
    </Layout>
  );
};

const SettingsTab = ({ cycle, onSaved, canWrite }) => {
  const [form, setForm] = useState({
    name: cycle.name,
    start_date: cycle.start_date,
    end_date: cycle.end_date,
    notes: cycle.notes || '',
    status: cycle.status,
    year_of_enrollment: cycle.year_of_enrollment,
    year_of_study: cycle.year_of_study,
    course_length: cycle.course_length,
    course_type: cycle.course_type,
    default_num_assessments: cycle.default_num_assessments || '',
    default_assessment_due_dates: cycle.default_assessment_due_dates || [],
    phases: cycle.phases || [],
  });
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    setForm({
      name: cycle.name, start_date: cycle.start_date, end_date: cycle.end_date,
      notes: cycle.notes || '', status: cycle.status,
      year_of_enrollment: cycle.year_of_enrollment, year_of_study: cycle.year_of_study,
      course_length: cycle.course_length, course_type: cycle.course_type,
      default_num_assessments: cycle.default_num_assessments || '',
      default_assessment_due_dates: cycle.default_assessment_due_dates || [],
      phases: cycle.phases || [],
    });
  }, [cycle]);

  const multiPhase = (form.phases || []).length > 0;

  const splitIntoPhases = () => {
    const dates = (form.default_assessment_due_dates || []).filter(Boolean);
    setForm({
      ...form,
      phases: [{ name: 'Phase 1', start_date: form.start_date, end_date: form.end_date,
        num_assessments: dates.length, assessment_due_dates: dates }],
    });
  };

  const revertToSinglePeriod = () => {
    const all = form.phases.flatMap((p) => p.assessment_due_dates || []).filter(Boolean).sort();
    setForm({ ...form, phases: [], default_assessment_due_dates: all, default_num_assessments: all.length || '' });
  };

  const cohortChanged =
    form.year_of_enrollment !== cycle.year_of_enrollment ||
    form.year_of_study !== cycle.year_of_study ||
    form.course_length !== cycle.course_length ||
    form.course_type !== cycle.course_type;

  const doSave = async () => {
    const blank = (form.phases || []).some((p) => !p.start_date || !p.end_date || (p.assessment_due_dates || []).some((d) => !d));
    if (blank) { toast.error('Every phase needs start/end dates and a date for each assessment'); return; }
    setSaving(true);
    try {
      const payload = { ...form, phases: (form.phases || []).map((p) => ({ ...p, num_assessments: Number(p.num_assessments) || 0 })) };
      // Coerce default_num_assessments blank → null
      payload.default_num_assessments = payload.default_num_assessments === '' ? null : Number(payload.default_num_assessments);
      await cyclesAPI.update(cycle.id, payload);
      toast.success('Cycle updated');
      setConfirmOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!cohortChanged) {
      await doSave();
      return;
    }
    // Preview impact first
    try {
      const r = await cyclesAPI.previewImpact(cycle.id, {
        year_of_enrollment: form.year_of_enrollment,
        year_of_study: form.year_of_study,
        course_length: form.course_length,
        course_type: form.course_type,
      });
      setImpact(r.data);
      setConfirmOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview impact');
    }
  };

  const seedDefaultDates = () => {
    if (!form.start_date) { toast.error('Set a start date first'); return; }
    const n = Number(form.default_num_assessments) || 4;
    const start = new Date(form.start_date);
    const dates = Array.from({ length: n }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + 30 * (i + 1));
      return d.toISOString().slice(0, 10);
    });
    setForm({ ...form, default_assessment_due_dates: dates });
  };

  const [resyncing, setResyncing] = useState(false);
  const resyncAssessments = async () => {
    setResyncing(true);
    try {
      const r = await cyclesAPI.resyncAssessments(cycle.id);
      const s = r.data;
      toast.success(`Re-synced ${s.placements} placement${s.placements === 1 ? '' : 's'} — ${s.moved} moved, ${s.created} added, ${s.removed} removed`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-sync failed');
    } finally {
      setResyncing(false);
    }
  };

  return (
    <Card><CardContent className="pt-6 space-y-4">
      <div>
        <Label>Cycle name</Label>
        <Input data-testid="settings-name" value={form.name} disabled={!canWrite} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>

      <div className="pt-2 border-t">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Cohort fields — editing these re-computes the roster</p>
        <CohortFields form={form} setForm={setForm} disabled={!canWrite} testIdPrefix="settings" />
      </div>

      <div className="pt-2 border-t">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold inline-flex items-center gap-1">
            <CalendarRange className="w-3.5 h-3.5" /> Timeline — {multiPhase ? `${form.phases.length} phase${form.phases.length === 1 ? '' : 's'}` : 'single period'}
          </p>
          {canWrite && (
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="ghost" className="text-xs h-7 text-gray-600"
                data-testid="settings-resync-assessments-btn"
                disabled={resyncing}
                title="Re-align every placed student's pending assessment forms with the saved schedule"
                onClick={resyncAssessments}
              >
                {resyncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Re-sync assessment dates
              </Button>
              {multiPhase ? (
                <Button size="sm" variant="ghost" data-testid="settings-revert-single" onClick={revertToSinglePeriod} className="text-xs h-7">Revert to single period</Button>
              ) : (
                <Button size="sm" variant="outline" data-testid="settings-split-phases" onClick={splitIntoPhases} className="text-xs h-7">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Split into phases
                </Button>
              )}
            </div>
          )}
        </div>
        {multiPhase ? (
          <>
            <p className="text-[11px] text-gray-500 mb-2">Saving re-aligns pending assessment forms of placed students to these phase dates. Submitted assessments are never changed.</p>
            <PhasesEditor phases={form.phases} onChange={(phases) => setForm({ ...form, phases })} disabled={!canWrite} testIdPrefix="settings-phase" />
          </>
        ) : (
          <p className="text-[11px] text-gray-500 mb-2">Phases let one cycle run across semesters or academic years, each with its own start date and set of assessments.</p>
        )}
      </div>

      {!multiPhase && (<>
      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <div>
          <Label>Start date</Label>
          <Input data-testid="settings-start" type="date" value={form.start_date} disabled={!canWrite} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        </div>
        <div>
          <Label>End date</Label>
          <Input data-testid="settings-end" type="date" value={form.end_date} disabled={!canWrite} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
      </div>

      <div className="pt-2 border-t">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">
          Assessment schedule
        </p>
        <p className="text-xs text-gray-500 mb-2">
          Saving re-aligns pending assessment forms of placed students to these dates. Submitted assessments are never changed.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Number of assessments</Label>
            <Input
              data-testid="settings-num-assessments"
              type="number" min={0} max={20}
              disabled={!canWrite}
              value={form.default_num_assessments}
              onChange={(e) => setForm({ ...form, default_num_assessments: e.target.value })}
              placeholder="Fall back to assessment rules"
            />
          </div>
          <div className="flex items-end">
            {canWrite && (
              <Button size="sm" variant="outline" onClick={seedDefaultDates} data-testid="settings-seed-dates-btn">
                Seed dates from start + 30d
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
          {(form.default_assessment_due_dates || []).length === 0 ? (
            <p className="text-xs text-gray-400 italic">No default dates. New placements will use the assessment rule&apos;s interval.</p>
          ) : (
            form.default_assessment_due_dates.map((d, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`settings-due-${i}`}>
                <span className="w-6 h-6 shrink-0 rounded-full bg-ica-navy text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                <Input
                  type="date" value={d} disabled={!canWrite}
                  onChange={(e) => {
                    const next = [...form.default_assessment_due_dates];
                    next[i] = e.target.value;
                    setForm({ ...form, default_assessment_due_dates: next });
                  }}
                  className="flex-1"
                />
                {canWrite && (
                  <Button size="sm" variant="ghost" onClick={() => setForm({
                    ...form,
                    default_assessment_due_dates: form.default_assessment_due_dates.filter((_, idx) => idx !== i),
                  })} className="text-red-600 hover:bg-red-50">
                    <XCircle className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))
          )}
          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => {
              const last = form.default_assessment_due_dates.slice(-1)[0] || form.start_date;
              const next = new Date(last || Date.now());
              next.setDate(next.getDate() + 30);
              setForm({ ...form, default_assessment_due_dates: [...form.default_assessment_due_dates, next.toISOString().slice(0, 10)] });
            }} data-testid="settings-add-due">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add assessment
            </Button>
          )}
        </div>
      </div>
      </>)}

      <div>
        <Label>Status</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!canWrite}>
          <SelectTrigger data-testid="settings-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Notes</Label>
        <Input data-testid="settings-notes" value={form.notes} disabled={!canWrite} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      {canWrite && (
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving} >
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : cohortChanged ? 'Preview impact + Save' : 'Save changes'}
          </Button>
        </div>
      )}

      {/* Impact preview modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-ica-navy flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Confirm cohort change</DialogTitle>
            <DialogDescription>Editing cohort fields changes which students are in this cycle. Review the impact below before saving.</DialogDescription>
          </DialogHeader>
          {impact ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 rounded border bg-gray-50">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Roster before</div>
                  <div className="text-xl font-bold text-gray-800">{impact.current_student_count}</div>
                </div>
                <div className="p-2 rounded border bg-gray-50">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Roster after</div>
                  <div className="text-xl font-bold text-gray-800">{impact.new_student_count}</div>
                </div>
              </div>
              {impact.students_dropping_out?.length > 0 && (
                <div className="p-2 rounded border border-red-200 bg-red-50">
                  <div className="text-[11px] font-semibold text-red-700 mb-1">Dropping out ({impact.students_dropping_out.length})</div>
                  <ul className="text-[11px] text-red-800 space-y-0.5 max-h-24 overflow-y-auto">
                    {impact.students_dropping_out.map((s) => <li key={s.id}>· {s.name} ({s.student_number})</li>)}
                  </ul>
                </div>
              )}
              {impact.students_joining?.length > 0 && (
                <div className="p-2 rounded border border-emerald-200 bg-emerald-50">
                  <div className="text-[11px] font-semibold text-emerald-700 mb-1">Joining ({impact.students_joining.length})</div>
                  <ul className="text-[11px] text-emerald-800 space-y-0.5 max-h-24 overflow-y-auto">
                    {impact.students_joining.map((s) => <li key={s.id}>· {s.name} ({s.student_number})</li>)}
                  </ul>
                </div>
              )}
              {impact.will_cascade_break && (
                <div className="p-2 rounded border border-amber-300 bg-amber-50 text-[12px] text-amber-900">
                  <div className="font-semibold mb-1">⚠ {impact.orphaned_placements.length} placement(s) will become orphaned</div>
                  <div>{impact.orphaned_feedback_forms} feedback form(s) and {impact.orphaned_contracts} contract(s) linked to them. Resolve them from the Orphans tab after saving.</div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Loading impact…</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-cohort-change-btn" onClick={doSave} disabled={saving} >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

// ---------------------------------------------------------------------------
// Orphans tab — placements whose student no longer belongs to this cohort.
// Admin can move to another cycle, unlink from any cycle, or keep-and-exclude.
// ---------------------------------------------------------------------------
const OrphansTab = ({ cycleId, canWrite, onChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allCycles, setAllCycles] = useState([]);
  const [pickerFor, setPickerFor] = useState(null); // orphan being moved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, cs] = await Promise.all([cyclesAPI.listOrphans(cycleId), cyclesAPI.list()]);
      setRows(o.data.orphans || []);
      setAllCycles(cs.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load orphans');
    } finally { setLoading(false); }
  }, [cycleId]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (placementId, body) => {
    try {
      await cyclesAPI.resolveOrphan(cycleId, placementId, body);
      toast.success('Orphan resolved');
      setPickerFor(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <Card><CardContent className="pt-6">
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-6">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm text-gray-700 font-medium">No orphans in this cycle</p>
          <p className="text-[11px] text-gray-500 mt-1">Every placement&apos;s student still matches the cycle&apos;s cohort fields.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="orphans-list">
          <p className="text-xs text-gray-600">
            The following placements are still linked to this cycle but their student no longer matches the cohort. Resolve each one.
          </p>
          {rows.map((o) => (
            <div key={o.placement_id} data-testid={`orphan-row-${o.placement_id}`} className="border rounded p-3 bg-white">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{o.student_name || '(deleted student)'}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {o.student_number} → {o.restaurant_name} · {o.start_date} → {o.end_date} · {o.status}
                  </div>
                  <div className="text-[11px] text-amber-700 mt-1 bg-amber-50 rounded px-2 py-1 inline-block">
                    {o.reason}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {o.feedback_form_count} feedback form(s), {o.contract_count} contract(s) linked
                  </div>
                </div>
                {canWrite && (
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" data-testid={`orphan-move-${o.placement_id}`} onClick={() => setPickerFor(o)}>
                      Move to cycle
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`orphan-unlink-${o.placement_id}`} onClick={() => resolve(o.placement_id, { action: 'unlink' })}>
                      Unlink from any cycle
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`orphan-keep-${o.placement_id}`} onClick={() => resolve(o.placement_id, { action: 'keep' })}>
                      Keep here (exclude student)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!pickerFor} onOpenChange={(o) => !o && setPickerFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Move placement to another cycle</DialogTitle>
            <DialogDescription>
              Pick a cycle whose cohort matches {pickerFor?.student_name}. Only cycles where the student&apos;s current profile matches will accept the move.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-72 overflow-y-auto divide-y">
            {allCycles
              .filter((c) => c.id !== cycleId && c.status !== 'archived')
              .filter((c) => (
                String(c.year_of_enrollment) === String(pickerFor?.student_current_cohort?.year_of_enrollment)
                && c.year_of_study === pickerFor?.student_current_cohort?.year_of_study
                && c.course_length === pickerFor?.student_current_cohort?.course_length
                && c.course_type === pickerFor?.student_current_cohort?.course_type
              ))
              .map((c) => (
                <li key={c.id} className="py-2 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-gray-500">{c.status} · {c.start_date} → {c.end_date}</div>
                  </div>
                  <Button size="sm" data-testid={`pick-target-cycle-${c.id}`} onClick={() => resolve(pickerFor.placement_id, { action: 'move', target_cycle_id: c.id })}>
                    Move here
                  </Button>
                </li>
              ))}
            {allCycles.filter((c) => c.id !== cycleId
              && String(c.year_of_enrollment) === String(pickerFor?.student_current_cohort?.year_of_enrollment)
              && c.year_of_study === pickerFor?.student_current_cohort?.year_of_study
              && c.course_length === pickerFor?.student_current_cohort?.course_length
              && c.course_type === pickerFor?.student_current_cohort?.course_type).length === 0 && (
              <p className="text-sm text-gray-500 italic py-4">No cycle currently matches this student&apos;s cohort. Create one first (or Unlink to detach).</p>
            )}
          </ul>
          <DialogFooter><Button variant="outline" onClick={() => setPickerFor(null)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

export default CycleDetail;
